import Skeleton from "react-loading-skeleton";
import { useCart } from "~/hooks/cart";
import { type DateLike, useStoreTime } from "~/hooks/hours";
import { useDispatchTypeLabel } from "~/hooks/language";
import { ChevronDownIcon } from "~/icons";
import type { Location, Page, TimeSlot } from "~/types";
import { Box } from "../Box/Box";
import { Text } from "../Text/Text";

export function OrderingOptionsButton(props: {
  location: Location;
  page: Page | null;
  onClickDetails: () => void;
}): React.ReactNode {
  const {
    dispatchOption,
    timeSlot: selectedTimeslot,
    // Could use the label from here if we fallback to PICK_UP inside
    // dispatchOptionLabel,
  } = useCart(props.location.ref);
  const { formatTime, formatDate, formatZone } = useStoreTime();

  // NOTE: Defaulting the label to Pick Up if there is no dispatch option selected, or none available
  // This is the happy path so the screen doesn't show an irrelevant loading state.
  // TODO: We should prompt user if none selected, right?
  const { label: dispatchLabel } = useDispatchTypeLabel(
    dispatchOption || "PICK_UP",
  );

  return (
    <Box
      onClick={props.onClickDetails}
      flexDirection="row"
      display="flex"
      alignItems="center"
      background="white"
      paddingY="2"
      paddingX="2"
      height="touchable"
      style={{
        borderRadius: "4px",
        boxShadow: "0px 0px 8px rgba(0,0,0,0.25)",
        // if you change this, change the location name max width too (below)
        maxWidth: 300,
      }}
    >
      <Box display={"flex"} flexDirection={"column"}>
        <Box display={"flex"} flexDirection={"row"}>
          <Text
            size="small"
            color={!dispatchOption ? "neutral-500" : "neutral-900"}
            transform="uppercase"
          >
            {dispatchLabel || <Skeleton width={75} />}
          </Text>
          <Box paddingLeft="1">
            <Text size="small">
              {selectedTimeslot
                ? collectionTimeSlotsSelectedDateFormat(
                    selectedTimeslot,
                    formatTime,
                    formatDate,
                    formatZone,
                  )
                : "ASAP"}
            </Text>
          </Box>
        </Box>
        <Box display={"flex"} flexDirection={"row"} style={{ maxWidth: 250 }}>
          <Text truncate={true} priority="low" transform="uppercase">
            {props.location.name || <Skeleton width={150} />}
          </Text>
        </Box>
      </Box>
      <Box
        style={{
          alignContent: "center",
          alignItems: "center",
        }}
        height="full"
        flexDirection={"column"}
        paddingLeft="2"
      >
        <ChevronDownIcon />
      </Box>
    </Box>
  );
}

function collectionTimeSlotsSelectedDateFormat(
  timeSlot: TimeSlot,
  formatTime: (date: DateLike) => string,
  formatDate: (date: DateLike, pattern?: string) => string,
  formatZone: (date: DateLike) => string,
): string {
  const startDate = formatDate(timeSlot.startTime, "ccc, d MMM");
  const startTime = formatTime(timeSlot.startTime);

  return `${startDate}, ${startTime} ${formatZone(timeSlot.startTime)}`;
}
